import React, { useEffect, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { CirclePlus } from "lucide-react";
import CustomInput from "../../UI/PageInput/CustomInput";
import CustomDescriptionInput from "../../UI/PageInput/CustomDescriptionInput";
import CustomFileInput from "../../UI/PageInput/CustomFileInput";
import SelectedImagesBox from "./SelectedImagesBox";
import CenteredHeading from "../../UI/headingsUI/CenteredHeading";
import GrayButton from "../../UI/buttonUI/GrayButton";
import BackDate from "../../UI/PageInput/BackDate";
import SearchDropdown from "../../UI/PageInput/SearchDropdown";
import ExpenseViewDialog from "../../UI/viewDialogs/expense/ExpenseViewDialog";
import UseDialogBox from "../../../CustomHooks/UseDialogBox";
import ImageSlider from "../../UI/viewDialogs/ImageSlider";
import { getCompressImage } from "../../../functions/getCompressImage";
import PrimaryButton from "../../UI/buttonUI/PrimaryButton";
import {
  noSpaceOnlyValidation,
  nonNegativeNumberValidation,
} from "../../../validations/formValidations";

const AddExpense = ({ sendData, expenseSetup, loading, successResponse }) => {
  const [isFormClear, setIsFormClear] = useState(false);
  const { showDialogBox, setShowDialogBox } = UseDialogBox();
  const [imageSliderPics, setImageSliderPics] = useState([]);
  const [imageSliderCurrentIndex, setImageSliderCurrentIndex] = useState();
  const [openViewDialog, setViewDialog] = useState(false);

  const userId = localStorage.getItem("userId");

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      user_id: userId,
      pictures: [], // Initialize pictures as an empty array
    },
  });

  let pic = watch("pictures");
  const files = watch("files");

  const { append, remove } = useFieldArray({
    control,
    name: "files",
  });
  const { category_id, receiver_id } = watch();

  useEffect(() => {
    getCompressImage(pic, append);
  }, [pic]);

  // handle clear form
  const handleClearForm = () => {
    setIsFormClear(!isFormClear);
    // reset form value
    reset();
  };

  const onSubmit = (data) => {
    if (data.receiver_id && data.category_id !== 1 && data.category_id !== 2) {
      delete data.receiver_id;
    }
    sendData(data, handleClearForm);
  };

  const openImageSlider = () => {
    setViewDialog(false);
    setShowDialogBox(true);
    setImageSliderPics(successResponse.pictureList);
  };

  const closeImageSlider = () => {
    setViewDialog(true);
    setShowDialogBox(false);
  };

  // show success dialog on api response
  useEffect(() => {
    if (successResponse) {
      setViewDialog(true);
    }
  }, [successResponse]);
  return (
    <div className="form-container">
      <CenteredHeading title="Add Expense" />
      <form
        className="2xs:max-lg:pt-0"
        onSubmit={!loading ? (e) => handleSubmit(onSubmit)(e) : undefined}
      >
        <div className="flex flex-wrap">
          <div className="leftCol">
            <CustomInput
              type="number"
              name="amount"
              labelText="Amount *"
              placeholder="Enter"
              register={{
                ...register("amount", {
                  required: "Please Enter an amount",
                  ...nonNegativeNumberValidation,
                }),
              }}
              error={errors.amount}
            />
            <SearchDropdown
              name="category_id"
              label="Category *"
              keyName="name"
              id="id"
              options={expenseSetup.categories}
              selectedVal={category_id}
              setValue={setValue}
              control={control}
              error={errors.category_id}
            />
            {(category_id === 1 || category_id === 2) && (
              <>
                <SearchDropdown
                  name="receiver_id"
                  label="Received By *"
                  keyName="fullName"
                  id="id"
                  options={expenseSetup.users}
                  selectedVal={receiver_id}
                  setValue={setValue}
                  control={control}
                  error={errors.receiver_id}
                  search={true}
                />
              </>
            )}
            <BackDate
              register={{
                ...register("date"),
              }}
              setValue={setValue}
              isFormClear={isFormClear}
            />
          </div>
          <div className="rightCol">
            <>
              <CustomFileInput
                name="pictures"
                placeholder="Add Image* (jpg, png)"
                accept=".png, .jpg, image/png, image/jpeg"
                register={{
                  ...register("pictures", {
                    required: files
                      ? files.length === 0
                        ? "Please choose a file"
                        : false
                      : "Please choose a file",
                  }),
                }}
                multiple={true}
                error={errors.pictures}
              />
              <SelectedImagesBox
                errors={errors.pictures}
                selectedImages={files}
                setValue={setValue}
                name="pictures"
                remove={remove}
              />
            </>
          </div>
        </div>
        <CustomDescriptionInput
          name="description"
          labelText="Description *"
          register={{
            ...register("description", {
              required: "Please Enter Description",
              ...noSpaceOnlyValidation,
            }),
          }}
          error={errors.description}
        />
        <div className="submitWrap">
          <p className="addWrap">
            <PrimaryButton
              btnText={!loading ? "Add Expense" : "Adding..."}
              loading={loading}
              Icon={CirclePlus}
              size={18}
            />
          </p>
          <p className="grayWrap">
            <GrayButton
              btnText="Clear"
              width="w-40"
              onClick={handleClearForm}
            />
          </p>
        </div>
      </form>
      {openViewDialog && (
        <ExpenseViewDialog
          viewDetails={successResponse}
          successMsg="Expense Successfully Created!"
          openImageSlider={openImageSlider}
          setImageSliderCurrentIndex={setImageSliderCurrentIndex}
        />
      )}
      {showDialogBox && (
        <ImageSlider
          title="Expense Photos"
          images={imageSliderPics}
          imageSliderCurrentIndex={imageSliderCurrentIndex}
          closeDialogBox={closeImageSlider}
        />
      )}
    </div>
  );
};
export default AddExpense;
